import {useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {BoardingPassCard} from '../../components/design-system'
import {AMENITIES, BED_CATEGORIES, BRAND, DEPOSIT, STEPS, CRASHPAD, TRANSPORT, WHY} from './site'
import {useLightbox} from './Lightbox'
import {useCrashpadAvailability} from './availability'
import {api, GalleryItem, listingImageSrc} from '../../services/api'
import './public.css'

// Shown until the admin-managed gallery loads (and if the API is unreachable).
const DEFAULT_GALLERY: GalleryItem[] = [
  {image: '/images/lounge.webp', label: 'Lounge & dining'},
  {image: '/images/kitchen.webp', label: 'Full kitchen'},
  {image: '/images/bunkroom.webp', label: 'The space'},
  {image: '/images/bathroom.webp', label: 'Shared bathroom'},
  {image: '/images/view.webp', label: 'City view'},
  {image: '/images/community.webp', label: 'Common area'},
]

const Container: React.FC<{children: React.ReactNode; style?: React.CSSProperties}> = ({children, style}) => (
  <div style={{maxWidth: 1140, margin: '0 auto', padding: '0 20px', ...style}}>{children}</div>
)

const SectionTitle: React.FC<{eyebrow?: string; title: string; sub?: string; center?: boolean}> = ({eyebrow, title, sub, center}) => (
  <div style={{textAlign: center ? 'center' : 'left', maxWidth: center ? 680 : undefined, margin: center ? '0 auto 40px' : '0 0 32px'}}>
    {eyebrow && (
      <div style={{color: BRAND.green, fontWeight: 700, fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10}}>{eyebrow}</div>
    )}
    <h2 style={{fontSize: 32, fontWeight: 800, color: BRAND.ink, margin: 0, lineHeight: 1.2}}>{title}</h2>
    {sub && <p style={{color: BRAND.muted, fontSize: 16, marginTop: 12, marginBottom: 0}}>{sub}</p>}
  </div>
)

const HomePage = () => {
  const {open} = useLightbox()
  const [gallery, setGallery] = useState<GalleryItem[]>(DEFAULT_GALLERY)
  useEffect(() => {
    api.getSiteGallery().then((r) => {
      if (r.gallery?.length) setGallery(r.gallery)
    }).catch(() => {})
  }, [])
  const galleryItems = gallery.map((g) => ({src: listingImageSrc(g.image), title: g.label}))
  const {beds, totalBeds, availableBeds, loading} = useCrashpadAvailability()
  // The room slug for each bed size (e.g. twin-room), taken live from the data
  // so each category card deep-links to its own type page, not the generic list.
  const slugForType = (type: (typeof BED_CATEGORIES)[number]['type']) =>
    beds.find((b) => b.type === type)?.slug ?? null

  return (
    <div>
      {/* Hero */}
      <section
        style={{
          position: 'relative',
          overflow: 'hidden',
          color: '#fff',
          backgroundImage:
            'linear-gradient(100deg, rgba(1,45,24,0.95) 0%, rgba(2,110,47,0.82) 48%, rgba(3,149,64,0.40) 100%), url(/images/lobby.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className='ccp-blob' style={{background: '#0ad66a', width: 360, height: 360, top: -120, right: -80}} />
        <Container style={{position: 'relative', padding: '90px 20px 84px'}}>
          <div className='row align-items-center g-5'>
            <div className='col-12 col-lg-6 ccp-fade-up'>
              <div style={{display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 999, padding: '7px 16px', fontSize: 13, fontWeight: 600, letterSpacing: '0.04em', marginBottom: 22, backdropFilter: 'blur(6px)'}}>
                ✈️ Crew lodging in San Francisco
              </div>
              <h1 style={{color: '#fff', fontSize: 'clamp(34px, 5vw, 52px)', fontWeight: 800, lineHeight: 1.08, margin: '0 0 18px', letterSpacing: '-0.02em'}}>
                Rest easy<br />between flights.
              </h1>
              <p style={{fontSize: 18, lineHeight: 1.6, opacity: 0.95, margin: '0 0 22px', maxWidth: 520}}>
                A safe, co-ed, verified crashpad for aviation professionals — one shared space in San Francisco, with convenient transportation to and from SFO.
              </p>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 14}}>
                <Link to='/rooms' className='ccp-cta-light' style={{textDecoration: 'none', fontWeight: 700, fontSize: 16, color: BRAND.green, background: '#fff', borderRadius: 12, padding: '15px 28px'}}>
                  Reserve a Bed
                </Link>
                <Link to='/rooms' style={{textDecoration: 'none', fontWeight: 700, fontSize: 16, color: '#fff', border: '1px solid rgba(255,255,255,0.6)', borderRadius: 12, padding: '15px 28px'}}>
                  View Bed Availability
                </Link>
              </div>
            </div>
            <div className='col-12 col-lg-6 d-flex justify-content-center justify-content-lg-end ccp-fade-up-2'>
              <BoardingPassCard
                title='Crosscheck CrashPad'
                subtitle='San Francisco · To & from SFO'
                docType='Boarding Pass'
                status={{label: loading ? 'Live availability' : `${availableBeds} of ${totalBeds} available`, tone: 'available'}}
                fields={[
                  {label: 'Crashpad', value: '8 beds'},
                  {label: 'Beds', value: 'Cold-Bed'},
                  {label: 'Access', value: 'Keyless'},
                ]}
                stubCode='✈'
                stubLabel='Crew'
              />
            </div>
          </div>

          {/* Inventory + live availability strip */}
          <div style={{display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 56, paddingTop: 28, borderTop: '1px solid rgba(255,255,255,0.18)'}}>
            {[
              {value: String(CRASHPAD.totalBeds), label: 'Total beds'},
              {value: loading ? '—' : String(availableBeds), label: 'Currently available'},
              {value: '~20 min', label: 'By car to/from SFO'},
              {value: '~45 min', label: 'By transit to/from SFO'},
            ].map((s) => (
              <div key={s.label} style={{flex: '1 1 140px'}}>
                <div style={{fontSize: 28, fontWeight: 800, lineHeight: 1}}>{s.value}</div>
                <div style={{fontSize: 13, opacity: 0.85, marginTop: 5, letterSpacing: '0.03em'}}>{s.label}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Why choose */}
      <section style={{padding: '72px 0'}}>
        <Container>
          <SectionTitle center eyebrow='Why Crosscheck' title='Built for the crew lifestyle' sub='Reserve-friendly, professionally managed lodging for pilots, flight attendants, and other aviation professionals.' />
          <div className='row g-4'>
            {WHY.map((w) => (
              <div className='col-12 col-md-4' key={w.title}>
                <div className='ccp-lift' style={{background: '#fff', border: '1px solid #eef0f4', borderRadius: 18, padding: '30px 26px', height: '100%', boxShadow: '0 6px 18px rgba(27,27,41,0.04)'}}>
                  <div style={{width: 56, height: 56, borderRadius: 16, background: 'linear-gradient(135deg, rgba(3,149,64,0.12), rgba(3,149,64,0.04))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 18}}>{w.icon}</div>
                  <h3 style={{fontSize: 19, fontWeight: 700, color: BRAND.ink, margin: '0 0 8px'}}>{w.title}</h3>
                  <p style={{color: BRAND.muted, fontSize: 15, lineHeight: 1.6, margin: 0}}>{w.text}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Accommodation — Cold-Bed categories with live counts */}
      <section style={{padding: '72px 0', background: BRAND.soft}}>
        <Container>
          <SectionTitle center eyebrow='Accommodations' title='Eight beds, one shared crashpad' sub={CRASHPAD.description} />
          <div className='row g-4 justify-content-center'>
            {BED_CATEGORIES.map((cat) => {
              const slug = slugForType(cat.type)
              return (
              <div className='col-12 col-md-4' key={cat.type}>
                <div className='ccp-lift' style={{background: '#fff', borderRadius: 18, padding: '28px 26px', height: '100%', borderTop: `5px solid ${BRAND.green}`, boxShadow: '0 10px 30px rgba(27,27,41,0.06)', textAlign: 'center'}}>
                  <h3 style={{fontSize: 22, fontWeight: 800, color: BRAND.ink, margin: '0 0 4px'}}>{cat.label} Beds</h3>
                  <div style={{color: BRAND.muted, fontSize: 14, marginBottom: 12}}>{cat.codes.length} total · {cat.codes.join(', ')}</div>
                  <div style={{display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4, marginBottom: 18}}>
                    <span style={{fontSize: 30, fontWeight: 800, color: BRAND.green}}>${cat.price}</span>
                    <span style={{fontSize: 14, fontWeight: 600, color: BRAND.muted}}>each / month</span>
                  </div>
                  <Link to={slug ? `/rooms/${slug}` : '/rooms'} className='ccp-cta' style={{display: 'block', textAlign: 'center', textDecoration: 'none', fontWeight: 700, color: '#fff', background: BRAND.green, borderRadius: 12, padding: '12px 24px'}}>
                    View availability
                  </Link>
                </div>
              </div>
              )
            })}
          </div>
          <p style={{textAlign: 'center', color: BRAND.muted, fontSize: 13, margin: '24px auto 0', maxWidth: 620}}>
            ⚠️ {DEPOSIT.warning}
          </p>
        </Container>
      </section>

      {/* Transportation highlights */}
      <section style={{padding: '72px 0'}}>
        <Container>
          <SectionTitle center eyebrow='Getting to SFO' title='Convenient transportation' />
          <div className='row g-4 justify-content-center'>
            {[TRANSPORT.car, TRANSPORT.transit].map((t) => (
              <div className='col-12 col-md-5' key={t.label}>
                <div className='ccp-lift' style={{background: '#fff', border: '1px solid #eef0f4', borderRadius: 18, padding: '26px 24px', height: '100%', display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 6px 18px rgba(27,27,41,0.04)'}}>
                  <span aria-hidden style={{fontSize: 32}}>{t.icon}</span>
                  <span style={{fontSize: 16, fontWeight: 600, color: BRAND.ink}}>{t.label}</span>
                </div>
              </div>
            ))}
          </div>
          <p style={{textAlign: 'center', color: BRAND.muted, fontSize: 13, marginTop: 18}}>{TRANSPORT.disclaimer}</p>
        </Container>
      </section>

      {/* How it works */}
      <section style={{padding: '72px 0', background: BRAND.soft}}>
        <Container>
          <SectionTitle center eyebrow='Getting started' title='How reservations work' sub='From availability to approval — the reservation flow.' />
          <div className='row g-4'>
            {STEPS.map((s) => (
              <div className='col-12 col-md-6 col-lg' key={s.n}>
                <div style={{height: '100%'}}>
                  <div style={{width: 52, height: 52, borderRadius: '50%', background: `linear-gradient(135deg, ${BRAND.green}, ${BRAND.greenDark})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 19, marginBottom: 16, boxShadow: '0 8px 18px rgba(3,149,64,0.28)'}}>{s.n}</div>
                  <h3 style={{fontSize: 17, fontWeight: 700, color: BRAND.ink, margin: '0 0 6px'}}>{s.title}</h3>
                  <p style={{color: BRAND.muted, fontSize: 14, lineHeight: 1.6, margin: 0}}>{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Amenities */}
      <section style={{padding: '72px 0'}}>
        <Container>
          <SectionTitle center eyebrow='On site' title='Everything included' sub='Shared crashpad comforts, kept clean for crew.' />
          <div className='row g-4'>
            {AMENITIES.map((a) => (
              <div className='col-6 col-md-4 col-lg-2' key={a.label}>
                <div className='ccp-lift' style={{background: '#fff', border: '1px solid #eef0f4', borderRadius: 16, padding: '24px 14px', textAlign: 'center', height: '100%', boxShadow: '0 4px 12px rgba(27,27,41,0.03)'}}>
                  <div style={{fontSize: 30, marginBottom: 10}}>{a.icon}</div>
                  <div style={{fontSize: 14, fontWeight: 600, color: BRAND.ink}}>{a.label}</div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Gallery */}
      <section style={{padding: '72px 0', background: BRAND.soft}}>
        <Container>
          <SectionTitle center eyebrow='Take a look' title='Inside the crashpad' sub='From the lounge to your bed — here is the space.' />
          <div className='row g-3'>
            {gallery.map((g, i) => (
              <div className='col-6 col-md-4' key={`${g.image}-${i}`}>
                <div className='ccp-lift ccp-zoom' onClick={() => open(galleryItems, i)} style={{position: 'relative', borderRadius: 16, overflow: 'hidden', aspectRatio: '4 / 3', boxShadow: '0 6px 18px rgba(27,27,41,0.08)', cursor: 'zoom-in'}}>
                  <img src={listingImageSrc(g.image)} alt={g.label} loading='lazy' style={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}} />
                  <div style={{position: 'absolute', left: 0, right: 0, bottom: 0, padding: '20px 14px 10px', background: 'linear-gradient(transparent, rgba(0,0,0,0.6))', color: '#fff', fontSize: 13, fontWeight: 600}}>
                    {g.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA band */}
      <section style={{position: 'relative', overflow: 'hidden', background: `linear-gradient(135deg, ${BRAND.greenDark}, ${BRAND.green})`, color: '#fff'}}>
        <div className='ccp-blob' style={{background: '#0ad66a', width: 320, height: 320, top: -140, left: '12%'}} />
        <div className='ccp-blob' style={{background: '#016b2f', width: 320, height: 320, bottom: -160, right: '8%'}} />
        <Container style={{position: 'relative', padding: '64px 20px', textAlign: 'center'}}>
          <h2 style={{color: '#fff', fontSize: 'clamp(26px, 4vw, 34px)', fontWeight: 800, margin: '0 0 12px', letterSpacing: '-0.01em'}}>Ready to rest easy between flights?</h2>
          <p style={{fontSize: 17, opacity: 0.95, margin: '0 auto 28px', maxWidth: 560}}>{DEPOSIT.short}</p>
          <Link to='/rooms' className='ccp-cta-light' style={{textDecoration: 'none', fontWeight: 700, fontSize: 16, color: BRAND.green, background: '#fff', borderRadius: 12, padding: '15px 32px', display: 'inline-block'}}>
            Reserve a Bed
          </Link>
        </Container>
      </section>
    </div>
  )
}

export {HomePage}
